import React, { useState, useEffect, useRef } from 'react';
import '../styles/despHeader.css';

// Declare feather as a global variable
declare global {
  interface Window {
    feather?: {
      replace: () => void;
    };
  }
}

interface DespHeaderProps {
  userEmail?: string;
  onLogout?: () => void;
  onLogin?: () => void;
  portalUrl?: string;
  iamUrl?: string;
  serviceName?: string;
}

const DespHeader: React.FC<DespHeaderProps> = ({
  userEmail,
  onLogout,
  onLogin,
  portalUrl = 'https://platform.destine.eu',
  iamUrl = 'https://auth.destine.eu',
  serviceName = 'sencore'
}) => {
  const [headerInfoMenuIsVisible, setHeaderInfoMenuIsVisible] = useState(false);
  const [headerUserMenuIsVisible, setHeaderUserMenuIsVisible] = useState(false);
  const [dropdownIsVisible, setDropdownIsVisible] = useState(false);
  const [menuIconState, setMenuIconState] = useState('menu');
  const [headerHasRevealed, setHeaderHasRevealed] = useState(false);

  const headerRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement | null>(null);
  const footerContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionPrec = useRef(0);
  const tempPrevPos = useRef(0);
  const hidingTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const userIsAuthenticated = !!userEmail;
  const hideTimeout = 2000;

  // Get CSS variables
  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      mainContainerRef.current = document.getElementById('main-container') as HTMLDivElement;
      footerContainerRef.current = document.getElementById('footer-container') as HTMLDivElement;
    }
  }, []);

  // Initialize feather icons
  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, []);

  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, [menuIconState]);

  // Header show/hide on scroll functionality
  useEffect(() => {
    const handleScroll = () => {
      const header = headerRef.current;
      if (!header) return;

      const body = document.querySelector('body');
      const backgroundColor = getComputedStyle(body!).getPropertyValue('--desp-background-color');
      const backgroundColorTransparent = getComputedStyle(body!).getPropertyValue('--desp-background-color-transparent');
      const headerHeight = getComputedStyle(body!).getPropertyValue('--header-height');

      const deltaY = window.scrollY - tempPrevPos.current;

      setTimeout(() => {
        if (window.scrollY < 200) {
          header.style.backgroundColor = backgroundColor;
        }
      }, 500);

      if (deltaY > 0 && deltaY < 100) {
        setTimeout(() => {
          scrollPositionPrec.current = window.scrollY;
        }, 500);

        if (headerHasRevealed) {
          if (hidingTimeoutId.current) {
            clearTimeout(hidingTimeoutId.current);
          }
          setHeaderHasRevealed(false);
          header.style.transition = 'top 500ms ease-in-out, background-color 500ms ease-in-out';
          header.style.top = "-" + headerHeight;
          setTimeout(() => {
            header.style.position = 'static';
            header.style.backgroundColor = backgroundColor;
            setHeaderInfoMenuIsVisible(false);
            setHeaderUserMenuIsVisible(false);
          }, 500);
        }
      } else if (deltaY < 0 && deltaY > -100) {
        if (window.scrollY >= 200) {
          header.style.backgroundColor = backgroundColorTransparent;
          header.style.transition = 'top 250ms ease-in-out, background-color 500ms ease-in-out';
          header.style.top = '0px';
          header.style.position = 'sticky';
          setTimeout(() => {
            setHeaderHasRevealed(true);
          }, 500);

          if (hidingTimeoutId.current) {
            clearTimeout(hidingTimeoutId.current);
          }
          hidingTimeoutId.current = setTimeout(() => {
            header.style.transition = 'top 500ms ease-in-out, background-color 500ms ease-in-out';
            header.style.top = "-" + headerHeight;
          }, hideTimeout);
        }
      }

      tempPrevPos.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headerHasRevealed, hideTimeout]);

  // Header mouse events
  const handleHeaderMouseOver = () => {
    if (hidingTimeoutId.current) {
      clearTimeout(hidingTimeoutId.current);
    }
  };

  const handleHeaderMouseLeave = () => {
    const header = headerRef.current;
    if (!header) return;

    const body = document.querySelector('body');
    const headerHeight = getComputedStyle(body!).getPropertyValue('--header-height');

    hidingTimeoutId.current = setTimeout(() => {
      header.style.transition = 'top 500ms ease-in-out, background-color 500ms ease-in-out';
      header.style.top = "-" + headerHeight;
    }, hideTimeout);
  };

  const toggleInfoMenu = () => {
    setHeaderInfoMenuIsVisible(!headerInfoMenuIsVisible);
    if (!headerInfoMenuIsVisible) {
      setHeaderUserMenuIsVisible(false);
    }
  };

  const toggleUserMenu = () => {
    if (headerUserMenuIsVisible) {
      setHeaderUserMenuIsVisible(false);
    } else {
      if (userIsAuthenticated) {
        setHeaderUserMenuIsVisible(true);
        setHeaderInfoMenuIsVisible(false);
      } else {
        if (onLogin) {
          onLogin();
        }
      }
    }
  };

  const toggleMobileMenu = () => {
    const header = headerRef.current;
    const mainContainer = mainContainerRef.current;
    const footerContainer = footerContainerRef.current;

    if (hidingTimeoutId.current) {
      clearTimeout(hidingTimeoutId.current);
    }

    if (dropdownIsVisible) {
      setMenuIconState('menu');
      setDropdownIsVisible(false);
      if (mainContainer) mainContainer.style.display = 'block';
      if (footerContainer) footerContainer.style.display = 'flex';
    } else {
      setMenuIconState('x');
      setDropdownIsVisible(true);
      if (mainContainer) mainContainer.style.display = 'none';
      if (footerContainer) footerContainer.style.display = 'none';
      if (header) {
        const body = document.querySelector('body');
        const backgroundColor = getComputedStyle(body!).getPropertyValue('--desp-background-color');
        header.style.backgroundColor = backgroundColor;
      }
    }
  };

  return (
    <>
      {/* Header */}
      <div
        id="desp-header-container"
        ref={headerRef}
        onMouseOver={handleHeaderMouseOver}
        onMouseLeave={handleHeaderMouseLeave}
      >
        <div id="desp-header-inner-container">
          {/* Left container */}
          <div id="left-container">
            <a href={portalUrl}>
              <img id="eu-flag-de" alt="destine platform home" src="/desp-assets/destination_earth_logo.svg" />
            </a>
            <a href="/" style={{
              textDecoration: 'none',
              color: 'var(--desp-font-color)',
              fontFamily: 'Roboto-Bold, sans-serif',
              fontSize: 'var(--desp-font-size)',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center'
            }}>
              SenCore
            </a>
          </div>

          {/* Central container - can be removed if no links needed */}
          <div id="central-container">
            {/* Navigation links removed as per requirements */}
          </div>

          {/* Right container */}
          <div id="right-container">
            {/* Info container */}
            <div id="info-container">
              <div id="info-button" onClick={toggleInfoMenu}>
                <i data-feather="info" id="info-icon"></i>
              </div>

              {/* Info Menu */}
              <div
                id="info-frame"
                style={{
                  display: headerInfoMenuIsVisible ? 'flex' : 'none',
                  opacity: headerInfoMenuIsVisible ? 1 : 0
                }}
              >
                <a id="info-docs" className="frame-link" href={`${portalUrl}/services/documents-and-api/doc/?service_name=${serviceName}`}>Docs</a>
                <a id="info-api" className="frame-link" href={`${portalUrl}/services/documents-and-api/api/?service_name=${serviceName}`}>API</a>
                <a id="info-support" className="frame-link" href={`${portalUrl}/support/`}>Support</a>
              </div>
            </div>

            {/* User container */}
            <div id="user-container">
              {userIsAuthenticated && (
                <>
                  <div id="login-div" onClick={toggleUserMenu}>
                    <a className="login-a logged">
                      <i data-feather="user" className="login-icon logged"></i>
                      <p id="login-displayed-name" className="login-text logged">{userEmail}</p>
                    </a>
                  </div>

                  {/* User Menu */}
                  <div
                    id="user-frame"
                    className="logged"
                    style={{
                      display: headerUserMenuIsVisible ? 'flex' : 'none',
                      opacity: headerUserMenuIsVisible ? 1 : 0
                    }}
                  >
                    <a id="user-frame-account-settings" className="frame-link" href={`${iamUrl}/realms/desp/account/`}>Account Settings</a>
                    <a id="user-frame-logout" className="frame-link" onClick={onLogout} style={{ cursor: 'pointer' }}>Logout</a>
                  </div>
                </>
              )}
            </div>

            {/* Mobile menu icon */}
            <div id="menu-icon-container" onClick={toggleMobileMenu}>
              <i data-feather={menuIconState} id="menu-icon"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Container */}
      <div
        id="dropdown-menu-container"
        style={{ display: dropdownIsVisible ? 'flex' : 'none' }}
      >
        <div className="dropdown-link">
          <a href="/">Home</a>
        </div>
        <div className="dropdown-link">
          <a href={`${portalUrl}/services/documents-and-api/doc/?service_name=${serviceName}`}>Docs</a>
        </div>
        <div className="dropdown-link">
          <a href={`${portalUrl}/services/documents-and-api/api/?service_name=${serviceName}`}>API</a>
        </div>
        <div className="dropdown-link">
          <a href={`${portalUrl}/support/`}>Support</a>
        </div>
      </div>
    </>
  );
};

export default DespHeader;
